import { useMemo } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import {
  ArrowLeft,
  ExternalLink,
  Play,
  Settings,
  GitBranch,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
} from 'lucide-react';
import { useProject, useProjectWorkflows, useTriggerWorkflow } from '@/hooks';
import { DataTable } from '@/components/DataTable';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Skeleton,
  EmptyState,
} from '@/components/ui';
import {
  formatRelativeTime,
  formatDuration,
  capitalize,
} from '@/lib/utils';
import type { Workflow, WorkflowStatus } from '@/types';

const columnHelper = createColumnHelper<Workflow>();

export function ProjectDetail() {
  const { id } = useParams({ from: '/projects/$id' });
  const { data: project, isLoading: isLoadingProject } = useProject(id);
  const { data: workflows, isLoading: isLoadingWorkflows } = useProjectWorkflows(id);
  const triggerWorkflow = useTriggerWorkflow();

  const workflowColumns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Workflow',
        cell: (info) => (
          <span className="font-medium text-foreground">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => {
          const status = info.getValue();
          return <WorkflowStatusBadge status={status} />;
        },
      }),
      columnHelper.accessor('trigger', {
        header: 'Trigger',
        cell: (info) => (
          <span className="text-foreground-muted capitalize">
            {info.getValue().replace('_', ' ')}
          </span>
        ),
      }),
      columnHelper.accessor('startedAt', {
        header: 'Started',
        cell: (info) => (
          <span className="text-foreground-muted">
            {formatRelativeTime(info.getValue())}
          </span>
        ),
      }),
      columnHelper.accessor('duration', {
        header: 'Duration',
        cell: (info) => {
          const duration = info.getValue();
          return (
            <span className="text-foreground-muted">
              {duration !== null ? formatDuration(duration) : '-'}
            </span>
          );
        },
      }),
    ],
    []
  );

  if (isLoadingProject) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-72" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="space-y-6">
        <Link
          to="/projects"
          className="inline-flex items-center gap-2 text-foreground-muted hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Projects
        </Link>
        <Card variant="bordered">
          <CardContent className="py-12">
            <EmptyState
              title="Project not found"
              description="The project you're looking for doesn't exist or has been deleted."
              action={
                <Link to="/projects">
                  <Button variant="secondary">View All Projects</Button>
                </Link>
              }
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleTriggerWorkflow = () => {
    triggerWorkflow.mutate({
      projectId: project.id,
      workflowName: 'default',
    });
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Link
        to="/projects"
        className="inline-flex items-center gap-2 text-foreground-muted hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Projects
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">{project.name}</h1>
            <Badge variant="status" status={project.status}>
              {capitalize(project.status)}
            </Badge>
          </div>
          <p className="text-foreground-muted mt-1">{project.description}</p>
          <a
            href={project.repository}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-accent hover:underline mt-2"
          >
            {project.repository.replace('https://github.com/', '')}
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={handleTriggerWorkflow}
            isLoading={triggerWorkflow.isPending}
          >
            <Play className="h-4 w-4" />
            Run Workflow
          </Button>
          <Button variant="ghost" size="md">
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card variant="bordered">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-background-tertiary">
                <GitBranch className="h-5 w-5 text-foreground-muted" />
              </div>
              <div>
                <p className="text-sm text-foreground-muted">Default Branch</p>
                <p className="font-medium text-foreground">
                  {project.settings.defaultBranch}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card variant="bordered">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-background-tertiary">
                <Clock className="h-5 w-5 text-foreground-muted" />
              </div>
              <div>
                <p className="text-sm text-foreground-muted">Total Runs</p>
                <p className="font-medium text-foreground">
                  {project.stats.totalWorkflows}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card variant="bordered">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-success/10">
                <CheckCircle className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="text-sm text-foreground-muted">Successful</p>
                <p className="font-medium text-foreground">
                  {project.stats.successfulRuns}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card variant="bordered">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-error/10">
                <XCircle className="h-5 w-5 text-error" />
              </div>
              <div>
                <p className="text-sm text-foreground-muted">Failed</p>
                <p className="font-medium text-foreground">
                  {project.stats.failedRuns}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Workflows table */}
      <Card variant="bordered">
        <CardHeader>
          <CardTitle>Recent Workflows</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoadingWorkflows ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : workflows && workflows.length > 0 ? (
            <DataTable
              columns={workflowColumns}
              data={workflows}
              pageSize={10}
            />
          ) : (
            <EmptyState
              title="No workflows yet"
              description="Trigger a workflow to see it appear here."
              action={
                <Button onClick={handleTriggerWorkflow} isLoading={triggerWorkflow.isPending}>
                  <Play className="h-4 w-4" />
                  Run First Workflow
                </Button>
              }
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function WorkflowStatusBadge({ status }: { status: WorkflowStatus }) {
  const config: Record<
    WorkflowStatus,
    { variant: 'success' | 'error' | 'warning' | 'info' | 'default'; icon: React.ReactNode }
  > = {
    success: {
      variant: 'success',
      icon: <CheckCircle className="h-3 w-3" />,
    },
    failed: {
      variant: 'error',
      icon: <XCircle className="h-3 w-3" />,
    },
    running: {
      variant: 'info',
      icon: <Loader2 className="h-3 w-3 animate-spin" />,
    },
    pending: {
      variant: 'default',
      icon: <Clock className="h-3 w-3" />,
    },
    cancelled: {
      variant: 'warning',
      icon: <XCircle className="h-3 w-3" />,
    },
  };

  const { variant, icon } = config[status];

  return (
    <Badge variant={variant} className="gap-1">
      {icon}
      {capitalize(status)}
    </Badge>
  );
}
